import drawingCheckPng from "./images/ai-drawing-check.png";
import drawingCheckWebp from "./images/ai-drawing-check.webp";
import similarityPng from "./images/ai-similarity-search.png";
import similarityWebp from "./images/ai-similarity-search.webp";
import dfmeaPng from "./images/ai-dfmea.png";
import dfmeaWebp from "./images/ai-dfmea.webp";
import fixturePng from "./images/ai-adaptive-fixture.png";
import fixtureWebp from "./images/ai-adaptive-fixture.webp";

/**
 * AI capabilities shown on the /aiml page.
 *
 * Marketing copy, deliberately brief: a one-line problem, a one-line answer,
 * and the outcome in words. No efficiency percentages or timing figures, and
 * no customer, partner or OEM names anywhere in this file — keep it that way.
 */
const aiCapabilities = [
  {
    id: "cad-intelligence",
    title: "AI-Powered CAD Intelligence",
    lead: "An AI assistant that works inside your live CAD session.",
    body: "It reads the full model tree as you work — sketches, features, bodies and assemblies — and responds to plain-language questions about the design. Ask it what is wrong with a part, how to model something to standard, or whether a change is manufacturable, without leaving CAD.",
    outcome:
      "Standardised modelling practice and less reliance on a handful of expert modellers.",
    platforms: ["CATIA", "CREO", "NX", "AutoCAD"],
  },
  {
    id: "drawing-check",
    img: drawingCheckPng,
    webp: drawingCheckWebp,
    alt:
      "Drawing comparison tool showing a master and modified drawing side by side with differences highlighted",
    title: "AI Drawing Check",
    lead: "Automatic validation of drawings against your design rules.",
    body: "Checking dimensions, annotations, GD&T and standards compliance by eye is slow and easy to get wrong, and the errors that slip through are found late. An AI checker reviews every drawing the same way, every time, and reports what does not conform.",
    outcome:
      "Consistent drawing quality and issues caught before release rather than after.",
  },
  {
    id: "similarity-search",
    img: similarityPng,
    webp: similarityWebp,
    alt:
      "Shape search results ranking existing parts by geometric similarity to an uploaded model",
    title: "3D Shape Similarity Search",
    lead: "Find the parts you have already designed.",
    body: "Engineers routinely rebuild geometry that already exists somewhere in the library because there is no practical way to search by shape. This searches your 3D models by the geometry itself and surfaces the closest existing matches.",
    outcome: "Genuine reuse of proven designs instead of quietly rebuilding them.",
  },
  {
    id: "feature-detection",
    title: "Feature and Object Detection in 3D Models",
    lead: "Automatic recognition of features across a model.",
    body: "Locating specific features or objects through a large 3D assembly by hand is slow and inconsistent between engineers. This identifies them automatically and reports where they are.",
    outcome: "A repeatable answer that does not depend on who is looking.",
  },
  {
    id: "regulation-compliance",
    title: "Regulation Compliance Checking",
    lead: "Marks and symbols read straight from PDF drawings.",
    body: "Compliance review means working through drawing sets interpreting marks, symbols and callouts manually — detailed work where attention naturally drifts. This detects and interprets them automatically and flags what needs attention.",
    outcome: "Compliance review that stays thorough across a full drawing set.",
  },
  {
    id: "dfmea-validation",
    img: dfmeaPng,
    webp: dfmeaWebp,
    alt:
      "Validation flow linking a released design to standards compliance, DFMEA geometry and a risk score",
    title: "DFMEA-Linked CAD Validation",
    lead: "Design risk connected directly to geometry.",
    body: "DFMEA usually lives in a document with no link to the model, so design changes are not risk-validated and non-conformance is discovered after release. This embeds the check in CAD, ties geometry to known failure modes, and flags high-risk areas as the design changes.",
    outcome: "Risk visible while the design can still change cheaply.",
  },
];

/**
 * Capabilities still in development. Listed separately and labelled, rather
 * than presented alongside the shipping work — claiming them as available
 * would be overstating it.
 */
export const aiInDevelopment = [
  {
    id: "adaptive-fixture",
    img: fixturePng,
    webp: fixtureWebp,
    alt:
      "Fixture design flow from new CAD through similarity matching and reuse classification to a generated fixture",
    title: "Adaptive Fixture Design",
    lead: "New programme, existing fixture knowledge.",
    body: "When a new programme arrives, the system identifies the closest previously executed programme, detects the geometric differences, and works out what that means for the fixture — sorting components into reusable, modifiable and genuinely new before generating an optimised design.",
  },
  {
    id: "variant-reduction",
    title: "Variant Intelligence and Complexity Reduction",
    lead: "Stop releasing parts you already have.",
    body: "Similar parts get created and released without anyone checking what already exists across CAD and PLM, and variants multiply — carrying BOM complexity, inventory and procurement cost with them. This finds functionally equivalent parts across systems and recommends reuse or consolidation before a duplicate is released.",
  },
];

export default aiCapabilities;
